/**
 * Lifecycle state of a validation run.
 * @public
 */
export type ValidationStatus = 'pending' | 'done' | 'aborted';

/**
 * Why a validation run was triggered.
 * @public
 */
export type ValidationTrigger =
  | 'change'
  | 'blur'
  | 'manual'
  | 'submit'
  | 'initial';

/**
 * Where a {@link ValidationError} originated.
 * @public
 */
export type ValidationErrorSource = 'native' | 'strato' | 'custom';

/**
 * Stable, comparable identity for a validity key.
 *
 * @remarks
 * Use {@link ValidityStateKey.equals} for comparisons. `===` fails across
 * module boundaries since each instance is a distinct object reference.
 *
 * @public
 */
export class ValidityStateKey<T extends string = string> {
  constructor(readonly value: T) {}

  /** Value equality check. Safe across module boundaries. */
  equals(other: ValidityStateKey): boolean {
    return this.value === other.value;
  }

  toString(): string {
    return this.value;
  }
}

/**
 * Browser-native validity keys, wrapping the same values as the native
 * {@link https://developer.mozilla.org/en-US/docs/Web/API/ValidityState | ValidityState}.
 * @public
 */
export const NativeValidityStateKeys = {
  valueMissing: new ValidityStateKey('valueMissing'),
  typeMismatch: new ValidityStateKey('typeMismatch'),
  patternMismatch: new ValidityStateKey('patternMismatch'),
  tooLong: new ValidityStateKey('tooLong'),
  tooShort: new ValidityStateKey('tooShort'),
  rangeUnderflow: new ValidityStateKey('rangeUnderflow'),
  rangeOverflow: new ValidityStateKey('rangeOverflow'),
  stepMismatch: new ValidityStateKey('stepMismatch'),
  badInput: new ValidityStateKey('badInput'),
  customError: new ValidityStateKey('customError'),
} as const;

/**
 * Union of the browser-native validity keys.
 * @public
 */
export type NativeValidityStateKey =
  (typeof NativeValidityStateKeys)[keyof typeof NativeValidityStateKeys];

/**
 * Strato-specific validity keys used by components whose validation cannot be
 * represented by browser-native validity states.
 * @public
 */
export const StratoValidityStateKeys = {
  syntaxError: new ValidityStateKey('syntaxError'),
  minMaxBoundary: new ValidityStateKey('minMaxBoundary'),
  invalidOrder: new ValidityStateKey('invalidOrder'),
  invalid: new ValidityStateKey('invalid'),
  invalidFrom: new ValidityStateKey('invalidFrom'),
  invalidTo: new ValidityStateKey('invalidTo'),
  invalidFromTo: new ValidityStateKey('invalidFromTo'),
  missingFrom: new ValidityStateKey('missingFrom'),
  missingTo: new ValidityStateKey('missingTo'),
  arrayIdentifierError: new ValidityStateKey('arrayIdentifierError'),
  unavailable: new ValidityStateKey('unavailable'),
  needsUpdate: new ValidityStateKey('needsUpdate'),
  missingVariables: new ValidityStateKey('missingVariables'),
  invalidVariables: new ValidityStateKey('invalidVariables'),
  genericError: new ValidityStateKey('genericError'),
} as const;

/**
 * Union of Strato-specific validity keys.
 * @public
 */
export type StratoValidityStateKey =
  (typeof StratoValidityStateKeys)[keyof typeof StratoValidityStateKeys];

/**
 * Options accepted by the {@link ValidationError} constructor.
 * @public
 */
export type ValidationErrorOptions = ErrorOptions & {
  /** Where the error came from. @defaultValue `'custom'` */
  source?: ValidationErrorSource;
  /** Stable error identity. @defaultValue `NativeValidityStateKeys.customError` */
  validityStateKey?: ValidityStateKey;
  /** ID(s) of all form control inputs affected by this error. */
  formControlId?: string | string[];
};

/**
 * A single validation error.
 *
 * @remarks
 * Extends `Error` so that `instanceof` narrowing works across package
 * boundaries and the original async failure can be preserved via `Error.cause`.
 *
 * @public
 */
export class ValidationError extends Error {
  /** Where the error came from. */
  readonly source: ValidationErrorSource;
  /** Stable error identity; always compare via {@link ValidityStateKey.equals}. */
  readonly validityStateKey: ValidityStateKey;
  /** ID(s) of all form control inputs affected by this error. */
  readonly formControlId: string | string[] | undefined;

  constructor(message: string, options: ValidationErrorOptions = {}) {
    super(message, { cause: options.cause });
    this.name = 'ValidationError';
    this.source = options.source ?? 'custom';
    this.validityStateKey =
      options.validityStateKey ?? NativeValidityStateKeys.customError;
    this.formControlId = options.formControlId;
  }
}

/**
 * Shared validation result type returned by `validateAsync()` and passed to
 * `onValidityChange`.
 * @public
 */
export type ValidationState = {
  /** Validation lifecycle state. */
  status: ValidationStatus;
  /** Why validation fired. */
  trigger: ValidationTrigger;
  /** Validation errors. Empty when the control is valid or still `'pending'`. */
  errors: ValidationError[];
};
